"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { usePathname } from "next/navigation";

import { getTabSessionId } from "@repo/ui/lib/tabSessionId";

import { useSharedContent } from "@repo/store/slices";

import { TabLimitModal } from "./modal/TabLimitModal";

const MAX_TABS = 3;
const BROADCAST_CHANNEL_NAME = "tab_session_channel";
const STORAGE_KEY = "booking_active_tabs_v2";
const LEGACY_STORAGE_KEY = "booking_active_tabs";
const TAB_TRACKER_ID_KEY = "booking_tracker_tab_id";
const HEARTBEAT_INTERVAL_MS = 10000;
const STALE_TAB_THRESHOLD_MS = 180000;
const BOOKING_FLOW_SEGMENTS = new Set([
  "flight",
  "baggage",
  "seats",
  "extras",
  "passengers",
  "payment",
]);

type BroadcastMessage = { type: "UPDATE" };
type ActiveTabEntry = {
  ownerInstanceId: string;
  openedAt: number;
  lastSeen: number;
  path: string;
};
type ActiveTabsMap = Record<string, ActiveTabEntry>;

const createId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export default function TabSessionInitializer({
  shouldTrackTabs = false,
}: {
  shouldTrackTabs?: boolean;
}) {
  const pathname = usePathname();

  const sharedContent = useSharedContent();
  const resolvedModalTitle = sharedContent?.tabLimitationTitle;
  const resolvedModalFirstParagraph = sharedContent?.tabLimitationMessage;
  const resolvedModalSecondParagraph = sharedContent?.tabLimitationActionGuidance;
  const pathSegments = pathname.split("/").filter(Boolean);
  const isBookingFlowPath = pathSegments.some((segment) => BOOKING_FLOW_SEGMENTS.has(segment));
  // Tracking is enabled only for booking flow routes.
  const isTrackingEnabled = shouldTrackTabs && isBookingFlowPath;

  const [isLimitReached, setIsLimitReached] = useState(false);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionInitRef = useRef(false);
  const instanceIdRef = useRef<string>(createId());
  const trackerTabIdRef = useRef<string>("");

  const getTrackerTabId = useCallback((): string => {
    if (trackerTabIdRef.current) {
      return trackerTabIdRef.current;
    }

    let trackerId = "";
    try {
      trackerId = sessionStorage.getItem(TAB_TRACKER_ID_KEY) || "";
    } catch {
      trackerId = "";
    }

    if (!trackerId) {
      trackerId = createId();
      try {
        sessionStorage.setItem(TAB_TRACKER_ID_KEY, trackerId);
      } catch {
        // Ignore storage failures; in-memory ref is enough for current page lifetime.
      }
    }

    trackerTabIdRef.current = trackerId;
    return trackerId;
  }, []);

  const getStoredTabs = useCallback((): ActiveTabsMap => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: unknown = JSON.parse(stored);

        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed as ActiveTabsMap;
        }
      }

      // Legacy migration path: old format is string[] of tab IDs.
      const legacyStored = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!legacyStored) {
        return {};
      }

      const parsedLegacy: unknown = JSON.parse(legacyStored);
      if (!Array.isArray(parsedLegacy)) {
        return {};
      }

      const now = Date.now();
      const migrated: ActiveTabsMap = {};
      parsedLegacy
        .filter((id): id is string => typeof id === "string" && id.length > 0)
        .forEach((id) => {
          migrated[id] = {
            ownerInstanceId: "legacy",
            openedAt: now,
            lastSeen: now,
            path: pathname,
          };
        });

      return migrated;
    } catch {
      return {};
    }
  }, [pathname]);

  const saveStoredTabs = useCallback((tabs: ActiveTabsMap) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  }, []);

  const pruneStaleTabs = useCallback((tabs: ActiveTabsMap, currentTabId: string): ActiveTabsMap => {
    const now = Date.now();

    const cleaned: ActiveTabsMap = {};
    Object.entries(tabs).forEach(([id, entry]) => {
      if (id === currentTabId) {
        cleaned[id] = entry;
        return;
      }

      if (now - entry.lastSeen <= STALE_TAB_THRESHOLD_MS) {
        cleaned[id] = entry;
      }
    });

    return cleaned;
  }, []);

  // Core logic to update storage and determine status
  const updateTabState = useCallback(() => {
    if (!isTrackingEnabled) return;

    let currentTrackerId = getTrackerTabId();
    const instanceId = instanceIdRef.current;
    const now = Date.now();

    let tabs = getStoredTabs();
    tabs = pruneStaleTabs(tabs, currentTrackerId);
    const keysBefore = Object.keys(tabs).sort().join("|");

    // If duplicated tab copied the same tracker ID, rotate to a fresh ID.
    const existing = tabs[currentTrackerId];
    if (
      existing &&
      existing.ownerInstanceId !== instanceId &&
      now - existing.lastSeen <= STALE_TAB_THRESHOLD_MS
    ) {
      currentTrackerId = createId();
      trackerTabIdRef.current = currentTrackerId;

      try {
        sessionStorage.setItem(TAB_TRACKER_ID_KEY, currentTrackerId);
      } catch {
        // Ignore storage failures; current in-memory ID remains unique.
      }
    }

    const openedAt = tabs[currentTrackerId]?.openedAt ?? now;
    tabs[currentTrackerId] = {
      ownerInstanceId: instanceId,
      openedAt,
      lastSeen: now,
      path: pathname,
    };

    saveStoredTabs(tabs);

    const keysAfter = Object.keys(tabs).sort().join("|");
    const activeCount = Object.keys(tabs).length;
    const shouldShowWarning = activeCount > MAX_TABS;

    if (shouldShowWarning !== isLimitReached) {
      console.log(
        `[TabLimit] Tab count: ${activeCount}, Limit: ${MAX_TABS}, ` +
          `Show warning: ${shouldShowWarning}, Current tab: ${currentTrackerId.slice(0, 8)}...`,
      );
    }

    setIsLimitReached(shouldShowWarning);

    return { activeCount, structuralChanged: keysBefore !== keysAfter };
  }, [
    getStoredTabs,
    getTrackerTabId,
    isLimitReached,
    isTrackingEnabled,
    pathname,
    pruneStaleTabs,
    saveStoredTabs,
  ]);

  // Function to broadcast updates to other tabs
  const broadcastUpdate = useCallback(() => {
    channelRef.current?.postMessage({ type: "UPDATE" });
  }, []);

  // Keep heartbeat running while tracking is enabled so tabs do not get
  // prematurely pruned before the 4th tab check runs.
  const manageHeartbeat = useCallback(() => {
    if (!isTrackingEnabled) return;

    // Start heartbeat if not already running
    if (!heartbeatIntervalRef.current) {
      heartbeatIntervalRef.current = setInterval(() => {
        updateTabState();
      }, HEARTBEAT_INTERVAL_MS);
    }
  }, [isTrackingEnabled, updateTabState]);

  // Combined update and broadcast
  const performUpdate = useCallback(() => {
    const result = updateTabState();
    if (result?.structuralChanged) {
      broadcastUpdate();
    }
    manageHeartbeat();
  }, [updateTabState, broadcastUpdate, manageHeartbeat]);

  const unregisterSelf = useCallback(() => {
    if (!isTrackingEnabled) return;
    const currentTrackerId = getTrackerTabId();
    const tabs = getStoredTabs();

    if (tabs[currentTrackerId]) {
      delete tabs[currentTrackerId];
    }

    saveStoredTabs(tabs);
    broadcastUpdate();
  }, [broadcastUpdate, getStoredTabs, getTrackerTabId, isTrackingEnabled, saveStoredTabs]);

  useEffect(() => {
    // Ensure session ID is set and notify components
    if (!sessionInitRef.current) {
      sessionInitRef.current = true;

      const existingSessionId = getTabSessionId();

      if (existingSessionId) {
        // Dispatch event immediately to notify waiting API calls
        // This is important when navigating from main app to flight app
        window.dispatchEvent(
          new CustomEvent("session-initialized", {
            detail: { sessionId: existingSessionId },
          }),
        );
      } else {
        console.warn(
          "[TabSessionInitializer] No session ID found - should be initialized by main app",
        );
        // Dispatch error event so waiting components don't hang
        window.dispatchEvent(new CustomEvent("session-initialized", { detail: { error: true } }));
      }
    }

    getTrackerTabId();
    channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);

    // CRITICAL: Don't clean up Redux data on pagehide/beforeunload
    // event.persisted is unreliable for cross-domain navigation
    // Only unregister from active tabs list when leaving
    const handlePageHide = () => {
      // Unregister from active tabs list (only if tracking is enabled)
      // This lets other tabs know this tab is gone, but doesn't delete Redux state
      if (isTrackingEnabled) {
        unregisterSelf();
      }
    };

    // Register pagehide for proper cleanup detection
    window.addEventListener("pagehide", handlePageHide);

    if (isTrackingEnabled) {
      // Initial registration - register this tab
      performUpdate();
      broadcastUpdate();

      // Listen for updates from other tabs
      channelRef.current.onmessage = (event: MessageEvent<BroadcastMessage>) => {
        if (event.data.type === "UPDATE") {
          // When another tab opens/closes, re-evaluate our position
          // Small delay to ensure localStorage is updated
          setTimeout(() => {
            updateTabState();
            manageHeartbeat();
          }, 50);
        }
      };

      // Update when tab becomes visible (user switches back to this tab)
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          performUpdate();
        }
      };

      // Register event listeners
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        // Cleanup
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("pagehide", handlePageHide);

        // Unregister this tab
        if (isTrackingEnabled) {
          unregisterSelf();
        }

        // Clear heartbeat interval if running
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Close broadcast channel
        channelRef.current?.close();
      };
    }

    return () => {
      // Always remove pagehide listener on unmount
      window.removeEventListener("pagehide", handlePageHide);

      // Clear heartbeat interval on component unmount
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      channelRef.current?.close();
    };
  }, [
    getTrackerTabId,
    isTrackingEnabled,
    performUpdate,
    broadcastUpdate,
    unregisterSelf,
    updateTabState,
    manageHeartbeat,
  ]);

  if (isLimitReached && isTrackingEnabled) {
    return (
      <TabLimitModal
        open
        title={resolvedModalTitle}
        firstParagraph={resolvedModalFirstParagraph}
        secondParagraph={resolvedModalSecondParagraph}
      />
    );
  }

  return null;
}
